import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorService } from '../shared/services/error.service';

export interface VehicleServiceOrderStatusChange {
  id: number;
  initialStatusName: string;
  configOrderId: number | null;
  /**
   * Observação: o campo no backend está como "NewStatusVeichile" (sic).
   */
  newStatusVeichile: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface VehicleServiceOrderStatusChangeCreate {
  initialStatusName: string;
  newStatusVeichile: string;
  configOrderId: number;
}

export interface VehicleServiceOrderStatusChangeUpdate {
  initialStatusName?: string;
  newStatusVeichile?: string;
  configOrderId?: number;
}

@Injectable({ providedIn: 'root' })
export class VehicleServiceOrderStatusChangesService {
  private readonly baseUrl = environment.microservices?.order || environment.apiUrl || '/api';
  private readonly resourcePath = 'vehicleserviceorderstatuschanges';

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    const headerMap: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headerMap['Authorization'] = `Bearer ${token}`;
    }

    return new HttpHeaders(headerMap);
  }

  private buildUrl(path: string): string {
    const [base, baseQuery] = String(this.baseUrl).split('?');
    const normalizedBase = base.replace(/\/+$/, '');
    const normalizedPath = String(path).replace(/^\/+/, '');
    let url = `${normalizedBase}/${normalizedPath}`;
    if (baseQuery) {
      url += `?${baseQuery}`;
    }
    return url;
  }

  /**
   * GET /vehicleserviceorderstatuschanges
   */
  getAll(): Observable<VehicleServiceOrderStatusChange[]> {
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders() }).pipe(
      map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao listar regras de mudança de status do veículo');
        return throwError(() => err);
      })
    );
  }

  /**
   * GET /vehicleserviceorderstatuschanges/{id}
   */
  getById(id: number): Observable<VehicleServiceOrderStatusChange> {
    return this.http.get<any>(this.buildUrl(`${this.resourcePath}/${id}`), { headers: this.getHeaders() }).pipe(
      map(res => this.mapFromDto(this.extractItem(res))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar regra de status do veículo');
        return throwError(() => err);
      })
    );
  }

  /**
   * GET /vehicleserviceorderstatuschanges?configOrderId={configOrderId}
   */
  getByConfigOrderId(configOrderId: number): Observable<VehicleServiceOrderStatusChange[]> {
    const params = new HttpParams().set('configOrderId', String(configOrderId));
    return this.http.get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders(), params }).pipe(
      map(res => this.extractArray(res).map(dto => this.mapFromDto(dto))),
      catchError(err => {
        // Alguns backends retornam 404 quando não há itens; tratar como lista vazia.
        if (err?.status === 404) {
          return of([] as VehicleServiceOrderStatusChange[]);
        }

        this.errorService.showErrorMessage('Erro ao listar regras por configuração');
        return throwError(() => err);
      })
    );
  }

  /**
   * POST /vehicleserviceorderstatuschanges
   */
  create(payload: VehicleServiceOrderStatusChangeCreate): Observable<VehicleServiceOrderStatusChange> {
    const headers = this.getHeaders();

    return this.http.post<any>(this.buildUrl(this.resourcePath), payload, { headers }).pipe(
      map(res => this.mapFromDto(this.extractItem(res))),
      tap(() => this.errorService.showSuccessMessage('Regra criada com sucesso')),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar regra');
        return throwError(() => err);
      })
    );
  }

  /**
   * PUT /vehicleserviceorderstatuschanges/{id}
   */
  update(id: number, payload: VehicleServiceOrderStatusChangeUpdate): Observable<VehicleServiceOrderStatusChange> {
    const headers = this.getHeaders();

    return this.http.put<any>(this.buildUrl(`${this.resourcePath}/${id}`), payload, { headers }).pipe(
      map(res => this.mapFromDto(this.extractItem(res))),
      tap(() => this.errorService.showSuccessMessage('Regra atualizada com sucesso')),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar regra');
        return throwError(() => err);
      })
    );
  }

  /**
   * DELETE /vehicleserviceorderstatuschanges/{id}
   */
  delete(id: number): Observable<void> {
    return this.http.delete<void>(this.buildUrl(`${this.resourcePath}/${id}`), { headers: this.getHeaders() }).pipe(
      map(() => void 0),
      tap(() => this.errorService.showSuccessMessage('Regra removida com sucesso')),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao remover regra');
        return throwError(() => err);
      })
    );
  }

  private extractArray(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.data?.items)) return response.data.items;
    if (Array.isArray(response?.vehicleServiceOrderStatusChanges)) return response.vehicleServiceOrderStatusChanges;
    if (Array.isArray(response?.VehicleServiceOrderStatusChanges)) return response.VehicleServiceOrderStatusChanges;
    if (Array.isArray(response?.data?.vehicleServiceOrderStatusChanges)) return response.data.vehicleServiceOrderStatusChanges;
    if (Array.isArray(response?.data?.VehicleServiceOrderStatusChanges)) return response.data.VehicleServiceOrderStatusChanges;
    return [];
  }

  private extractItem(response: any): any {
    return response?.data?.data ?? response?.data ?? response?.item ?? response?.vehicleServiceOrderStatusChange ?? response;
  }

  private mapFromDto(dto: any): VehicleServiceOrderStatusChange {
    const newStatusVeichile =
      dto?.NewStatusVeichile ??
      dto?.newStatusVeichile ??
      dto?.NewStatusVehicle ??
      dto?.newStatusVehicle ??
      '';

    return {
      id: dto?.Id ?? dto?.id ?? 0,
      initialStatusName: dto?.InitialStatusName ?? dto?.initialStatusName ?? '',
      configOrderId: dto?.ConfigOrderId ?? dto?.configOrderId ?? null,
      newStatusVeichile,
      createdAt: dto?.CreatedAt ?? dto?.createdAt ?? null,
      updatedAt: dto?.UpdatedAt ?? dto?.updatedAt ?? null
    };
  }
}
